<script setup>
import { onMounted, watch, reactive, ref } from 'vue';
import LineChartOptions from '~/utils/chartConfigs/lineChart.js';
import { addThousandsSeparator } from '~/utils/utils.js';
import Card from '~/components/Card.vue';
import InfoTooltip from '~/components/InfoTooltip.vue';
import LoadingSkeleton from '~/components/LoadingSkeleton.vue';
import CardLabel from '~/components/CardLabel.vue';

// Props
const props = defineProps({
    kaspaStockToFlow: { type: Array, default: [] },
    loading: { type: Boolean, default: false }
});

// Chart data
const chartRef = ref(null);
const chartOptions = reactive({ ...LineChartOptions });

// Customize chart colors and options
chartOptions.colors = ['#70C7BA']; // Kaspa color
chartOptions.yaxis = {
    labels: {
        style: {
            colors: '#9ca3af',
            fontSize: '12px'
        },
        formatter: value => addThousandsSeparator(value, 0)
    },
    logarithmic: true, // Logarithmic scale
    forceNiceScale: true // Helps with scale number formatting
};

chartOptions.tooltip = {
    x: {
        format: 'MMM yyyy'
    },
    y: {
        formatter: value => addThousandsSeparator(value, 0)
    }
};

// Annotation for current date line
chartOptions.annotations = {
    xaxis: [{
        x: new Date().getTime(),
        strokeDashArray: 0,
        borderColor: '#4ade80',
        label: {
            borderColor: '#4ade80',
            style: {
                color: '#fff',
                background: '#4ade80'
            },
            text: 'We are here'
        }
    }]
};

// Chart data
const series = ref([]);

// Reset the zoom level
const resetZoom = () => {
    if (chartRef.value) {
        chartRef.value.resetSeries();
    }
};

// Create the chart
const createChart = () => {
    series.value = [{
        name: 'Stock-to-Flow',
        data: props.kaspaStockToFlow || []
    }];
};

// Watch for data changes
watch(() => props.kaspaStockToFlow, () => {
    if (props.kaspaStockToFlow) {
        createChart();
    }
});

// On component mounted
onMounted(() => {
    if (props.kaspaStockToFlow) {
        createChart();
    }
});
</script>

<template>
    <Card>
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-4">
            <!-- Section Title -->
            <div class="flex items-center w-full sm:w-auto">
                <CardLabel text="Stock-to-Flow" />
                <InfoTooltip text="Stock-to-Flow ratio measures scarcity by comparing circulating supply (stock) to annual production (flow). Higher values indicate increased scarcity." />
            </div>

            <!-- Right Section -->
            <div class="hidden sm:flex items-center gap-2">
                <!-- Reset zoom button -->
                <button @click="resetZoom" class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-none transition-all duration-200 ease-in-out">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mr-2 text-blue-500" fill="rgba(59, 130, 246, 0.1)">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 3.75H6A2.25 2.25 0 0 0 3.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0 1 20.25 6v1.5m0 9V18A2.25 2.25 0 0 1 18 20.25h-1.5m-9 0H6A2.25 2.25 0 0 1 3.75 18v-1.5M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                    Reset Zoom
                </button>
            </div>
        </div>

        <!-- Chart Container with Fixed Height -->
        <div class="h-[300px]">
            <LoadingSkeleton v-if="props.loading" height="100%" width="100%" />
            <div v-else class="slide-in-up">
                <ClientOnly>
                    <VueApexCharts ref="chartRef" type="area" height="300" :options="chartOptions" :series="series" />
                </ClientOnly>
            </div>
        </div>
    </Card>
</template>